import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play } from "lucide-react";
import { http, formatError } from "@/lib/api";
import { mediaSrc, thumbSrc } from "@/lib/media";
import Lightbox from "@/components/Lightbox";
import LockedOverlay from "@/components/LockedOverlay";
import ETransferPanel from "@/components/ETransferPanel";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Gallery() {
  const { user } = useAuth();
  const [tab, setTab] = useState("photos"); // photos | videos
  const [filter, setFilter] = useState("all"); // all | public | premium
  const [items, setItems] = useState([]);
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });
  const [site, setSite] = useState(null);
  const [etransfer, setEtransfer] = useState(null);

  const load = async () => {
    try {
      const { data } = await http.get(`/media?type=${tab === "photos" ? "photo" : "video"}`);
      setItems(data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, [tab]);

  useEffect(() => {
    http.get("/site").then((r) => setSite(r.data));
  }, []);

  const filtered = items.filter((i) => {
    if (filter === "all") return true;
    if (filter === "public") return !i.is_premium;
    return i.is_premium;
  });

  const viewable = filtered.filter((i) => !i.locked);

  const openLightbox = (item) => {
    if (item.locked) return;
    const idx = viewable.findIndex((v) => v.id === item.id);
    setLightbox({ open: true, index: idx });
  };

  const handleUnlock = async (media_id) => {
    if (!user) {
      toast.error("Please sign in to unlock this content.");
      return;
    }
    try {
      const { data } = await http.post("/payments/unlock", {
        media_id,
        origin_url: window.location.origin,
      });
      setEtransfer({
        type: "unlock",
        amount: data.amount,
        client_email: data.client_email,
        reference_id: data.tx_id,
        label: data.label,
        details: data.label,
      });
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  return (
    <div className="pt-32 pb-24" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <div className="mb-12">
          <div className="text-[10px] tracking-[0.35em] uppercase text-pink-blush mb-3">Visual Diary</div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-champagne">
            The <span className="italic text-pink-hot">gallery</span>
          </h1>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {[
            { id: "photos", label: "Photos" },
            { id: "videos", label: "Videos" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`gallery-tab-${t.id}`}
              className={`px-5 py-2 text-[11px] tracking-[0.28em] uppercase rounded-sm transition-all ${
                tab === t.id
                  ? "bg-pink-hot text-white glow-pink"
                  : "bg-noir-surface text-champagne/70 border border-pink-hot/20 hover:border-pink-hot/50"
              }`}
            >
              {t.label}
            </button>
          ))}
          <div className="w-full sm:w-px sm:h-8 sm:mx-4 sm:bg-pink-hot/20" />
          {[
            { id: "all", label: "All" },
            { id: "public", label: "Free" },
            { id: "premium", label: "Premium" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              data-testid={`gallery-filter-${t.id}`}
              className={`px-4 py-2 text-[10px] tracking-[0.25em] uppercase rounded-sm transition-all ${
                filter === t.id
                  ? "text-pink-hot border-b border-pink-hot"
                  : "text-champagne/60 hover:text-pink-blush"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-32">
            <div className="font-display italic text-3xl text-champagne/60">Coming soon, darling.</div>
            <div className="text-sm text-champagne/50 mt-3">New content is uploaded weekly.</div>
          </div>
        )}

        <div className={`grid gap-3 ${tab === "photos" ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
          {filtered.map((item, i) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5, delay: (i % 8) * 0.06 }}
              className={`group relative overflow-hidden rounded-sm cursor-pointer ${
                tab === "photos" ? "aspect-[3/4]" : "aspect-video"
              }`}
              onClick={() => openLightbox(item)}
              data-testid={`media-item-${item.id}`}
            >
              {item.locked ? (
                <>
                  <img
                    src="https://images.pexels.com/photos/2732096/pexels-photo-2732096.jpeg"
                    alt="locked"
                    className="w-full h-full object-cover blur-sm scale-110"
                  />
                  <LockedOverlay
                    oneTimePrice={item.one_time_price}
                    onUnlock={() => handleUnlock(item.id)}
                  />
                </>
              ) : item.type === "photo" ? (
                <>
                  <img
                    src={mediaSrc(item)}
                    alt={item.caption || "photo"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-noir-base/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    {item.caption && (
                      <div className="absolute bottom-3 left-3 right-3 font-display italic text-champagne text-sm">
                        {item.caption}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="absolute inset-0 bg-noir-surface">
                    {(item.thumbnail_base64 || item.thumbnail_storage_path) ? (
                      <img src={thumbSrc(item)} alt="thumb" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-noir-surface to-noir-elevated" />
                    )}
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-noir-base/30 group-hover:bg-noir-base/50 transition-colors">
                    <div className="w-16 h-16 rounded-full bg-pink-hot/90 flex items-center justify-center group-hover:scale-110 transition-transform glow-pink">
                      <Play className="w-7 h-7 text-white ml-1" fill="white" />
                    </div>
                  </div>
                  {item.caption && (
                    <div className="absolute bottom-3 left-3 right-3 font-display italic text-champagne text-sm drop-shadow">
                      {item.caption}
                    </div>
                  )}
                </>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {lightbox.open && (
        <Lightbox
          items={viewable}
          index={lightbox.index}
          onClose={() => setLightbox({ open: false, index: 0 })}
          onPrev={() =>
            setLightbox((l) => ({ ...l, index: (l.index - 1 + viewable.length) % viewable.length }))
          }
          onNext={() =>
            setLightbox((l) => ({ ...l, index: (l.index + 1) % viewable.length }))
          }
        />
      )}
      {etransfer && site && (
        <ETransferPanel payment={etransfer} site={site} onClose={() => setEtransfer(null)} />
      )}
    </div>
  );
}
